import React, { useEffect, useState, useCallback } from "react";
import { api, fmtNum, fmtDate, errMsg } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { Label } from "@finance/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@finance/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@finance/components/ui/dialog";
import { toast } from "sonner";
import { Check, X, Lock, Receipt, Clock, Coins } from "@phosphor-icons/react";
import { KpiStrip, KpiCard, EmptyState } from "@finance/components/DesignSystem";

const KIND_LABEL = { payment: "Payment / Receipt", manual_journal: "Journal Entry", expense_payment: "Expense Payment" };
const STATUS_PILL = {
  Pending: "bg-amber-100 text-amber-700",
  Approved: "bg-emerald-100 text-emerald-700",
  Rejected: "bg-red-100 text-red-700",
};

export default function CashRequests() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("Pending");
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");

  const load = useCallback(() => {
    api.get(`/cash-requests?status=${tab === "all" ? "all" : tab}`).then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const approve = async (r) => {
    if (!window.confirm(`Approve ${KIND_LABEL[r.kind]} of TSh ${fmtNum(r.amount)} and post it to the cash ledger?`)) return;
    try { await api.post(`/cash-requests/${r.id}/approve`); toast.success("Approved & posted to the cash ledger"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const doReject = async () => {
    try { await api.post(`/cash-requests/${rejecting.id}/reject`, { reason }); toast.success("Request rejected"); setRejecting(null); setReason(""); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const pendingCount = rows.filter((r) => r.status === "Pending").length;

  return (
    <Layout>
      <PageHeader title="Cash Requests" subtitle="Approve or reject cash postings raised by other users — for Finance & Ronick only" />
      <div className="p-8">
        <div className="mb-4 flex items-center gap-2 rounded-sm border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/10 dark:text-amber-300">
          <Lock size={16} /> Direct cash posting is locked to Finance & Ronick. Everyone else's cash payments, journals and expense payments arrive here for review.
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="cash-requests-tabs">
            <TabsTrigger value="Pending" data-testid="tab-pending">Pending{tab === "Pending" && pendingCount ? ` (${pendingCount})` : ""}</TabsTrigger>
            <TabsTrigger value="Approved" data-testid="tab-approved">Approved</TabsTrigger>
            <TabsTrigger value="Rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="mt-6">
          <KpiStrip cols={3}>
            <KpiCard icon={Receipt} label="Requests" value={rows.length} testid="kpi-cr-count" />
            <KpiCard icon={Clock} label="Pending" value={pendingCount} tone="warning" testid="kpi-cr-pending" />
            <KpiCard icon={Coins} label="Total Amount" value={fmtNum(rows.reduce((s, r) => s + (r.amount || 0), 0), "TZS")} tone="accent" testid="kpi-cr-total" />
          </KpiStrip>
        </div>

        {rows.length === 0 ? (
          <EmptyState icon={Receipt} testid="cash-requests-empty" title="No cash requests" message="Cash postings raised by other users for review will appear here." />
        ) : (
        <div className="overflow-hidden rounded-sm border border-gray-200 bg-white dark:border-[#243049] dark:bg-[#0b1220]">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-[#0f1729]"><tr className="lx-label border-b border-gray-200 dark:border-[#243049]">
              <th className="px-4 py-2 text-left">Raised</th>
              <th className="px-4 py-2 text-left">Requested by</th>
              <th className="px-4 py-2 text-left">Type</th>
              <th className="px-4 py-2 text-left">Description</th>
              <th className="px-4 py-2 text-right">Amount</th>
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-right">Action</th>
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`cash-request-${r.id}`} className="border-b border-gray-100 hover:bg-gray-50 dark:border-[#1b2436] dark:hover:bg-[#131c30]">
                  <td className="px-4 py-2 text-gray-500">{fmtDate(r.created_at)}</td>
                  <td className="px-4 py-2 font-medium">{r.requested_by_name}</td>
                  <td className="px-4 py-2">{KIND_LABEL[r.kind] || r.kind}</td>
                  <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{r.memo}
                    {r.status === "Rejected" && r.reject_reason && <div className="text-xs text-red-500">Reason: {r.reject_reason}</div>}
                    {r.status !== "Pending" && r.reviewed_by && <div className="text-xs text-gray-400">by {r.reviewed_by} · {fmtDate(r.reviewed_at)}</div>}
                  </td>
                  <td className="px-4 py-2 text-right lx-num">{fmtNum(r.amount)}</td>
                  <td className="px-4 py-2"><span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_PILL[r.status]}`}>{r.status}</span></td>
                  <td className="px-4 py-2 text-right">
                    {r.status === "Pending" ? (
                      <div className="flex items-center justify-end gap-2">
                        <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-${r.id}`} onClick={() => approve(r)}><Check size={14} className="mr-1" />Approve</Button>
                        <Button size="sm" variant="outline" className="h-8 text-red-600" data-testid={`reject-${r.id}`} onClick={() => { setRejecting(r); setReason(""); }}><X size={14} className="mr-1" />Reject</Button>
                      </div>
                    ) : <span className="text-gray-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader><DialogTitle className="font-display">Reject cash request</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-500">{rejecting && `${KIND_LABEL[rejecting.kind]} · TSh ${fmtNum(rejecting.amount)} · ${rejecting.requested_by_name}`}</p>
          <div>
            <Label className="lx-label">Reason (optional)</Label>
            <Input className="mt-1" data-testid="reject-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Use the bank account instead" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejecting(null)}>Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700" data-testid="confirm-reject" onClick={doReject}>Reject request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
